import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';
import { useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { getUserFilterGroups, getUserFilterValues } from '../service';
import { IFieldOption, IFilterGroupItemResponse, IFilterValuesItemResponse, IUseFilterPanalDataProps, IUseFilterPanalDataReturn } from '../type';
import { FieldTypeIcon } from '@dbl/core/dataSource';

export const useFilterPanalData = (props: IUseFilterPanalDataProps): IUseFilterPanalDataReturn => {
  const {
    gameCode,
    gameCodeCacheKey = '__dbl_filter_panl_field_game_code__',
    fieldCacheKey = '__dbl_filter_panl_field__',
    stringCacheKey = '__dbl_filter_panl_string__',
    stringMapCacheKey = '__dbl_filter_panl_string_map__',
  } = props || {};
  const { formatMessage } = useIntl();
  // stringDataApiMap用于解决接口竞速问题
  const stringDataApiMap = useRef<{ [key: string]: IFilterValuesItemResponse[]; }>({});
  const [cacheGameCode, setCacheGameCode] = useLocalState<string>(gameCodeCacheKey, '');
  const [fieldLoading, setFieldLoading] = useState(false);
  const [fieldError, setFieldError] = useState(false);
  const [fieldData, setFieldData] = useLocalState<IFilterGroupItemResponse[]>(fieldCacheKey, []);
  const [stringLoading, setStringLoading] = useState(false);
  const [stringError, setStringError] = useState(false);
  const [stringData, setStringData] = useLocalState<IFilterValuesItemResponse[]>(stringCacheKey, []);
  const [stringDataMap, setStringDataMap] = useLocalState<{ [key: string]: IFilterValuesItemResponse[]; }>(stringMapCacheKey, {});

  useEffect(() => {
    // 如果gameCode变化，则清空缓存数据
    if (!gameCode || !cacheGameCode || gameCode === cacheGameCode) return;
    setFieldData([]);
    setStringData([]);
    setStringDataMap({});
  }, [cacheGameCode, gameCode]);

  const getFieldData = (gameCode: string, tableIds: string[]) => {
    setFieldLoading(true);
    setCacheGameCode(gameCode);
    const params = {
      game_code: gameCode,
      table_ids: tableIds,
    };
    return getUserFilterGroups(params).then(res => {
      setFieldData(res?.groups || []);
      setFieldLoading(false);
      setFieldError(false);
      return { data: res?.groups || [], error: false };
    }).catch(error => {
      console.error('Failed to get filter groups:', error);
      if (error?.code === 53004) {
        notification.warning({
          description: formatMessage({ id: 'dbl_filter_user_filter_effective' }),
        });
      }
      setFieldData([]);
      setFieldLoading(false);
      setFieldError(true);
      // 将失败状态返回给调用方
      return { data: [], error: true, errorInfo: error };
    });
  };

  const getStringData = (gameCode: string, filterId: string) => {
    setStringLoading(true);
    setCacheGameCode(gameCode);
    const params = {
      game_code: gameCode,
      filter_id: filterId,
    };
    return getUserFilterValues(params).then(res => {
      setStringData(res?.values);
      const newStringDataMap = { ...(stringDataApiMap.current || {}), ...(stringDataMap || {}) };
      newStringDataMap[filterId] = res?.values;
      stringDataApiMap.current = newStringDataMap;
      setStringDataMap(newStringDataMap);
      setStringLoading(false);
      setStringError(false);
      return { data: res?.values || [], error: false };
    }).catch(error => {
      console.error('Failed to get filter values:', error);
      setStringData([]);
      const newStringDataMap = { ...(stringDataApiMap.current || {}), ...(stringDataMap || {}) };
      newStringDataMap[filterId] = [];
      stringDataApiMap.current = newStringDataMap;
      setStringDataMap(newStringDataMap);
      setStringLoading(false);
      setStringError(true);
      // 将失败状态返回给调用方
      return { data: [], error: true, errorInfo: error };
    });
  };

  const fieldOption = useMemo(() => {
    const baseFieldOption: IFieldOption[] = [];
    const allFieldOption: IFieldOption[] = [];
    fieldData?.forEach(item => {
      const {
        group_id: groupId,
        group_name_en: groupNameEn,
        group_name_cn: groupNameCn,
        filters = []
      } = item || {};
      if (groupId !== null) {
        allFieldOption?.push({
          code: `all_group_${groupId}`,
          area_code: "all",
          area_name_en: "All",
          area_name_ch: "全部",
          disabled: true,
          type: 'string',
          name_ch: groupNameCn,
          name_en: groupNameEn,
        });
      }
      filters?.forEach(filter => {
        const { filter_id: filterId, name_cn: nameCn, data_type: type } = filter || {};
        const obj = {
          ...(filter || {}),
          code: filterId,
          type,
          svgIcon: FieldTypeIcon?.[type] || "abc",
          name_ch: nameCn,
          area_code: `group_${groupId}`,
          area_name_en: groupNameEn,
          area_name_ch: groupNameCn,
        };
        const allObj = {
          ...(obj || {}),
          code: `all_${filterId}`,
          area_code: 'all',
          area_name_en: "All",
          area_name_ch: "全部",
        };
        allFieldOption?.push(allObj);
        if (groupId !== null) {
          baseFieldOption?.push(obj);
        }
      });
    });
    const options = [...(allFieldOption || []), ...(baseFieldOption || [])];
    return options || [];
  }, [fieldData]);

  return {
    fieldLoading,
    fieldError,
    fieldData,
    stringLoading,
    stringError,
    stringData,
    fieldOption,
    stringDataMap,
    setFieldData,
    setStringData,
    getFieldData,
    getStringData,
  };
};